import { LinearGradient } from 'expo-linear-gradient'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View, StyleSheet, Platform } from 'react-native'
import Animated, { type SharedValue } from 'react-native-reanimated'

import { Image } from '@/src/elements/image'
import { Placeholder } from '@/src/elements/placeholder'
import { UserProfilePreview } from '@/src/elements/user-profile/UserProfilePreview/UserProfilePreview'
import { UserProfileWithModal } from '@/src/elements/user-profile/UserProfilePreviewWithModal'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { AccessibilityHiddenTitle } from '@/src/features/accessibility/ui/components/AccessibilityHiddenTitle'
import { TEST_ID } from '@/src/general/constants/testId'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TGetCommunityApiData } from '../../../../api/getCommunity.types'
import { CommunityProgressBar, type TCommunityProgressBarProps } from '../../../components/CommunityProgressBar'
import { CommunityStatusSection, type TCommunityStatusSectionProps } from '../../../components/CommunityStatusSection'
import { useAnimatedHeader } from '../../../hooks/useAnimatedHeader'
import { CommunityMessage } from './CommunityMessage'
import { HeaderTitle } from './HeaderTitle'

export type TCommunityListHeaderProps = {
  community?: TGetCommunityApiData
  offsetY: SharedValue<number>
  subscription: (TCommunityProgressBarProps & NonNullable<TCommunityStatusSectionProps['subscription']>) | undefined
  animated?: boolean
} & TSearchParamsParam

export const ListHeader = ({
  community,
  offsetY,
  searchParams,
  subscription,
  animated,
}: TCommunityListHeaderProps): ReactNode => {
  const { initializeRef } = useAccessibilityContext()
  const { opacityAnimatedStyle, imageAnimatedStyle } = useAnimatedHeader(offsetY)

  const { t } = useTranslate()

  const opacityStyle = animated ? opacityAnimatedStyle : undefined

  return (
    <>
      <Animated.View
        style={[
          {
            height: Platform.OS === 'web' ? 420 : 400,
            width: '100%',
          },
          animated ? imageAnimatedStyle : [],
        ]}
        className="rounded-b-xl rounded-t-none absolute top-0 left-0 right-0 bottom-0 overflow-hidden"
      >
        <Image
          className="rounded-none absolute"
          contentFit="cover"
          source={{ uri: community?.picture ? getImageUrl(community.picture) : '' }}
          testID={TEST_ID.MISCELLANEOUS.COMMUNITY_IMAGE}
        />
      </Animated.View>
      {/* List Header Placeholder */}
      {!community && <Placeholder className="absolute top-0 left-0 right-0 bottom-0 rounded-t-none" />}

      <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        {community && (
          <Animated.View style={[opacityStyle, StyleSheet.absoluteFill]}>
            <LinearGradient
              style={{ flex: 1 }}
              className="rounded-b-xl"
              start={{ x: 0, y: 1 }}
              end={{ x: 0, y: 0 }}
              colors={['#000000', '#FFFFFF00']}
            />
          </Animated.View>
        )}
        <View className="flex-col">
          <AccessibilityHiddenTitle
            ref={(ref) => {
              initializeRef(ref, 'main')
            }}
          >
            <FormattedMessage id="app.accessibility.community" />
          </AccessibilityHiddenTitle>
          <View className="w-screen max-w-full flex-col items-start gap-2 px-4 pb-4 pt-4 md:px-6">
            {community ? (
              <>
                <Animated.View
                  testID={TEST_ID.MISCELLANEOUS.COMMUNITY_TAGS}
                  style={[animated ? opacityAnimatedStyle : [], { maxWidth: '100%', overflow: 'hidden' }]}
                >
                  <CommunityStatusSection community={community} subscription={subscription} />
                </Animated.View>

                <HeaderTitle community={community} opacityStyle={opacityStyle} offsetY={offsetY} animated={animated} />
                <Animated.View style={opacityStyle} className="flex flex-row justify-center gap-2 mb-2">
                  <UserProfileWithModal user={community.user}>
                    <UserProfilePreview.Avatar
                      user={{ ...community.user, name: t('words.by-user', { userName: community.user.name || '' }) }}
                      style={{ text: { colors: 'white' }, icon: { size: 'sm' } }}
                    />
                  </UserProfileWithModal>
                </Animated.View>
                <Animated.View style={[opacityStyle, { width: '100%', maxHeight: 2 }]}>
                  <CommunityProgressBar progress={subscription?.progress} />
                </Animated.View>
                <CommunityMessage searchParams={searchParams} />
              </>
            ) : null}
          </View>
        </View>
      </View>
    </>
  )
}
